/**
 * Turn raw SpacetimeDB connection state into what the connection banner
 * shows (#243). Ported from the TurboTwin webclient's
 * `utils/connectionDiagnostics.ts` so both apps read a drop the same way.
 *
 * Pure: unit-tested in connectionStatus.test.ts.
 */

/** WebSocket close codes seen against SpacetimeDB, RFC 6455 plus the 44xx auth range. */
const WS_CLOSE_CODES: Record<string, string> = {
  '1000': 'normal closure',
  '1001': 'server going away (restart or shutdown)',
  '1005': 'no status received',
  '1006': 'the connection dropped without a close frame: usually a network blip or a server restart',
  '1008': 'policy violation, often a rejected or expired sign-in',
  '1011': 'server error',
  '1012': 'server restarting',
  '1013': 'server busy, try again later',
  '4401': 'sign-in invalid or expired',
  '4403': 'access denied for this module',
};

export function describeWsError(error: string): { closeCode: string | null; explanation: string | null } {
  const match = error.match(/code=(\d+)/);
  const closeCode = match ? match[1] : null;
  return { closeCode, explanation: closeCode ? (WS_CLOSE_CODES[closeCode] ?? null) : null };
}

export type ConnectionErrorKind = 'auth' | 'network' | 'unknown';

/**
 * Auth vs network vs unknown. A token-exchange failure carries its HTTP status
 * (`Failed to verify token: 429 …`): only a 401 is auth; a 429/503 is the
 * endpoint being unavailable, where Re-authenticate cannot help.
 */
export function classifyConnectionError(error: string): ConnectionErrorKind {
  const lower = error.toLowerCase();
  const { closeCode } = describeWsError(lower);
  const verifyStatus = /failed to verify token:\s*(\d{3})\b/.exec(lower);
  if (verifyStatus) return verifyStatus[1] === '401' ? 'auth' : 'network';
  if (
    lower.includes('authentication') ||
    lower.includes('unauthorized') ||
    lower.includes('token') ||
    closeCode === '4401' ||
    closeCode === '1008'
  ) {
    return 'auth';
  }
  if (lower.includes('network') || lower.includes('timeout') || lower.includes('connection') || closeCode === '1006') {
    return 'network';
  }
  return 'unknown';
}

/** One module's live state, as the banner reads it. */
export type ModuleState = {
  id: string;
  /** What the person calls it ("Orientations", "Site data", …). */
  label: string;
  isActive: boolean;
  error: string | null;
  /** False for a module that has not been connected yet (initial load). */
  everActive: boolean;
};

export type DownModule = ModuleState & { closeCode: string | null; explanation: string | null };

/**
 * Modules that are down right now: an error is set, or the module was
 * connected before and no longer is. A module still on its first connect
 * attempt with no error is loading, not down.
 */
export function downModules(states: readonly ModuleState[]): DownModule[] {
  return states
    .filter((m) => !!m.error || (m.everActive && !m.isActive))
    .map((m) => ({ ...m, ...describeWsError(m.error ?? '') }));
}

/** The banner's buttons for the modules that are down. */
export function bannerActions(down: readonly DownModule[], sessionExpired: boolean): {
  showRetry: boolean;
  showReauth: boolean;
} {
  const authProblem = down.some((m) => m.error && classifyConnectionError(m.error) === 'auth');
  return { showRetry: !sessionExpired, showReauth: sessionExpired || authProblem };
}
